import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  input,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { distinctUntilChanged, skip, Subscription } from "rxjs";
import {
  CampaignCodeClient,
  DealerViewModel,
  FindCampaignCodeResponse,
  MediaCategoryTypeNameEnum,
  MediaTypeNameEnum,
  ProductViewModelBase,
} from "src/domain/client";
import SettingNames from "src/domain/extentions/setting-names";
import { ChannelRequestService } from "src/services/channel-request.service";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import { ShoppingCartService } from "src/services/shopping-cart.service";
import { ThemeService } from "src/services/theme.service";
import { TextBoxComponent } from "../../inputs/text-box/text-box.component";
import { ButtonComponent } from "../../inputs/button/button.component";
import { AlertComponent } from "../alert/alert.component";
import { RouterLink } from "@angular/router";
import { ProductImageFallbackDirective } from "../../../directives/product-image-fallback.directive";
import { BrandCardComponent } from "../brand-card/brand-card.component";
import { DecimalPipe } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { MediaPipe } from "../../../pipes/media.pipe";
import { PricePipe } from "../../../pipes/price.pipe";
import { ProductImageURLPipe } from "../../../pipes/product-image-url.pipe";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "vw-shopping-cart",
  templateUrl: "./shopping-cart.component.html",
  imports: [
    TextBoxComponent,
    ButtonComponent,
    AlertComponent,
    RouterLink,
    ProductImageFallbackDirective,
    BrandCardComponent,
    DecimalPipe,
    TranslateModule,
    MediaPipe,
    PricePipe,
    ProductImageURLPipe,
  ],
})
export class ShoppingCartComponent implements OnInit, OnDestroy {
  readonly showCampaignCodeRow = input<boolean>(false);

  selectedDealer: DealerViewModel | null = null;
  private subscription = new Subscription();

  mediaCategoryTypeNames = MediaCategoryTypeNameEnum;
  mediaTypes = MediaTypeNameEnum;
  settingNames = SettingNames;

  campaignCodeResponse = signal<FindCampaignCodeResponse | undefined>(
    undefined,
  );
  readonly isInvalidCampaignCode = signal(false);

  campaignCodeForm = new FormGroup({
    campaignCode: new FormControl<string | undefined>(
      "",
      Validators.maxLength(100),
    ),
  });

  constructor(
    public modelAndDealerService: ModelAndDealerService,
    public shoppingCartService: ShoppingCartService,
    public themeService: ThemeService,
    public channelRequestService: ChannelRequestService,
  ) {}

  ngOnInit() {
    if (this.shoppingCartService.isCampaignCodeApplied()) {
      this.campaignCodeForm.controls.campaignCode.setValue(
        this.shoppingCartService.appliedCampaignCode(),
      );
    }

    this.subscription.add(
      this.modelAndDealerService.selectedDealer$
        .pipe(skip(1), distinctUntilChanged())
        .subscribe(() => {
          this.shoppingCartService.removeCampaignCode();
          this.campaignCodeForm.reset();
          this.isInvalidCampaignCode.set(false);
        }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async validateAndApplyOrRemoveCampaignCode(): Promise<void> {
    if (this.shoppingCartService.isCampaignCodeApplied()) {
      this.shoppingCartService.removeCampaignCode();
      this.campaignCodeForm.reset();
      this.isInvalidCampaignCode.set(false);
      return;
    }

    const campaignCodeToValidate =
      this.campaignCodeForm.controls.campaignCode.value ?? "";

    const response = await new CampaignCodeClient().findCampaignCode(
      campaignCodeToValidate,
      this.modelAndDealerService.selectedDealer?.number,
    );

    this.campaignCodeResponse.set(response);

    if (response.isValidCampaignCode) {
      this.isInvalidCampaignCode.set(false);
      this.shoppingCartService.applyCampaignCode(
        response.campaignCodeDetails!,
        campaignCodeToValidate,
      );
    } else {
      this.isInvalidCampaignCode.set(true);
    }
  }

  decreaseQuantity(shoppingCartItemId: number): void {
    this.shoppingCartService.decreaseQuantity(shoppingCartItemId);
  }

  increaseQuantity(event: MouseEvent, product: ProductViewModelBase): void {
    if (
      product.productId != null &&
      product.sku != null &&
      !(
        event.currentTarget instanceof Element &&
        event.currentTarget.classList.contains("disabled")
      )
    ) {
      this.shoppingCartService.addShoppingCartItem(
        product.productId,
        product.sku,
        product.minimumQuantity,
        product.media,
        1,
      );
    }
  }

  deleteItem(shoppingCartItemId: number): void {
    this.shoppingCartService.deleteItem(shoppingCartItemId);
  }
}
